import { getRole } from '@/store/selectors';
import { hasPerm, P } from '@/utilities/roles/permissions';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Flex,
  Link,
  ListItem,
  UnorderedList,
} from '@chakra-ui/react';
import { parseDate, formatDate, formatApplyScholarship, ScholarshipDisplay } from 'shared-components';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AgentApplicantAuthorisationStatus, ApplicantOfferConditionType, ApplicantOfferStatus, IApplicantOffer } from 'api-paths-cas';
import { getStatusReasonLabel } from '@/utilities/statusReasonLabel';
import { ApplicationState } from '../../../../store';
import { updateApplicantOfferLetterText } from '../../../../store/apply/actions';
import { Note, NoteType } from './Note';

type OfferLetterTextProps = {
  type: 'startText' | 'endText';
};

export function modifyStartTextConditionType(type: string, offer: IApplicantOffer, text?: string): string {
  // A grim and dirty hack to ensure the {conditionType} text is updated when a offer goes from condition to
  // unconditional or vice versa. There are quite a few other ways to handle this, such as exposing the offerText
  // template to the user but without UI changes that would also cause issues.
  // This has its risk but it's a stop gap to stop Unconditional offer letters including Conditional in them
  // when they should have said Unconditional.
  if (!text || type !== 'startText') {
    return text;
  }
  const isUnconditional = offer?.conditionType === ApplicantOfferConditionType.Unconditional;
  let finalText = text;
  const conditionalTextWithSpace = ` a ${ApplicantOfferConditionType.Conditional} `;
  const unconditionalTextWithSpace = ` an ${ApplicantOfferConditionType.Unconditional} `;
  if (isUnconditional) {
    finalText = text.replace(conditionalTextWithSpace, unconditionalTextWithSpace);
  } else {
    finalText = text.replace(unconditionalTextWithSpace, conditionalTextWithSpace);
  }
  return finalText;
}

export const OfferLetterText: React.FC<OfferLetterTextProps> = ({ type }) => {
  const [showDetails, setShowDetails] = useState(false);
  const toggleDetails = () => setShowDetails(!showDetails);
  const [isEdit, setIsEdit] = useState(false);
  const { application, agentAccessStatus } = useSelector((state: ApplicationState) => state.apply);

  const offer = useSelector((state: ApplicationState) => state.apply.offer);
  const dispatch = useDispatch();
  const params = useParams();
  const { offerId, userId, uni } = params;

  const role = useSelector(getRole);

  const isStartText = type === 'startText';
  const displayText = offer?.pendingChanges?.offerLetter?.[type] ?? offer?.offerLetter?.[type];

  const displayStartText = offer?.pendingChanges?.offerLetter?.startText ?? offer?.offerLetter?.startText;
  const displayEndText = offer?.pendingChanges?.offerLetter?.endText ?? offer?.offerLetter?.endText;
  // Big hack here, see function comment
  const text = modifyStartTextConditionType(type, offer, displayText);
  const [noteText, setNoteText] = useState(text);
  const offerStatus = offer?.status;
  const isWithdrawn = offerStatus === ApplicantOfferStatus.Withdrawn;
  const isRejected = offerStatus === ApplicantOfferStatus.Rejected;

  const isHeaderEditable = [
    ApplicantOfferStatus.Unconditional,
    ApplicantOfferStatus.Conditional,
    ApplicantOfferStatus.Applied,
    ApplicantOfferStatus.Draft, // TODO ENROLY-3304: Remove Draft after data migration completes
  ].includes(offerStatus);

  const entryPoint = offer?.pendingChanges?.programme?.entryPoint ?? offer?.programme?.entryPoint;
  const startDate = offer?.pendingChanges?.programme?.startDate ?? offer?.programme?.startDate;
  const endDate = offer?.pendingChanges?.programme?.endDate ?? offer?.programme?.endDate;
  const fee = offer?.pendingChanges?.programme?.fee ?? offer?.programme?.fee;
  const feeTotal = offer?.pendingChanges?.programme?.feeTotal ?? offer?.programme?.feeTotal;
  const scholarships = offer?.pendingChanges?.programme?.scholarships ?? offer?.programme?.scholarships;

  const isAuthorisationDenied = agentAccessStatus === AgentApplicantAuthorisationStatus.AUTHORISATION_DENIED;

  const canEditOffer = hasPerm(role, P.EDIT_APPLY_OFFER) && !isAuthorisationDenied;

  useEffect(() => {
    if (text) setNoteText(text);
  }, [text]);

  if ((isWithdrawn || isRejected) && !isStartText) {
    return null;
  }

  if (isWithdrawn || isRejected) {
    return (
      <Alert status="error" data-selector="offer-status-alert">
        <AlertIcon />
        <Flex direction={'column'}>
          <AlertTitle color="chakra-body-text" data-selector="offer-status-alert-title">
            {isWithdrawn ? ApplicantOfferStatus.Withdrawn : ApplicantOfferStatus.Rejected} (
            {getStatusReasonLabel(offer.statusReason)})
          </AlertTitle>
          {offer.statusExplanationToApplicant && (
            <>
              <AlertDescription fontWeight="semibold" mt={2}>
                Explanation to Applicant:
              </AlertDescription>
              <AlertDescription>{offer.statusExplanationToApplicant}</AlertDescription>
            </>
          )}
          {offer.statusInternalNote && (
            <>
              <AlertDescription fontWeight="semibold" mt={2}>
                Internal Note:
              </AlertDescription>
              <AlertDescription>{offer.statusInternalNote}</AlertDescription>
            </>
          )}
        </Flex>
      </Alert>
    );
  }

  if (!text) return null;

  if (isEdit && canEditOffer) {
    return (
      <Note
        status={NoteType.OfferLetterText}
        onNoteChange={(note: string) => {
          setNoteText(note);
        }}
        onSave={() => {
          dispatch(
            updateApplicantOfferLetterText({
              applicantId: userId,
              uni,
              offerId,
              startText: modifyStartTextConditionType('startText', offer, displayStartText),
              endText: displayEndText,
              [type]: noteText,
            }),
          );
          setIsEdit(false);
        }}
        noteText={noteText}
        onCancel={() => {
          setIsEdit(false);
        }}
        editMode={true}
        onEdit={() => {}}
        id={NoteType.OfferLetterText}
        index={0}
      />
    );
  }

  return (
    <Box py={3} px={6} backgroundColor="chakra-subtle-bg" mt={8} borderRadius="lg" color="muted" position="relative">
      {isHeaderEditable && canEditOffer && (
        <Box position="absolute" top={1} right={4}>
          <Button onClick={() => setIsEdit(true)}>Edit</Button>
        </Box>
      )}
      <Box mb={2}>{isStartText ? `Dear ${application.givenName},` : 'Regards,'}</Box>

      {text.split('\n').map((line, i) => (
        <Box key={i}>{line}</Box>
      ))}

      {isStartText && (
        <>
          {scholarships?.length > 0 && (
            <Box mt={2} mb={2}>
              <ScholarshipDisplay scholarships={scholarships} fee={fee} />
            </Box>
          )}

          <Box mt={2} mb={2}>
            Full details of the course are as follows: {!showDetails && <Link onClick={toggleDetails}>Details</Link>}
          </Box>
          <Collapse in={showDetails} animateOpacity>
            <Box>
              <UnorderedList>
                <ListItem>
                  Course: <b>{offer?.programme?.courseName}</b>
                </ListItem>
                {offer?.programme?.awardingBody && (
                  <ListItem>
                    Awarding body: <b>{offer.programme.awardingBody}</b>
                  </ListItem>
                )}
                <ListItem>
                  Mode: <b>{offer?.programme?.mode}</b>
                </ListItem>
                <ListItem>
                  Entry level: <b>{entryPoint}</b>
                </ListItem>
                <ListItem>
                  Start date: <b>{formatDate(parseDate(startDate), 'D MMM YYYY')}</b>
                </ListItem>
                <ListItem>
                  End date: <b>{formatDate(parseDate(endDate), 'D MMM YYYY')}</b>
                </ListItem>
                <ListItem>
                  Course fee (year 1): <b>£{fee?.toLocaleString() ?? '0'}</b>
                </ListItem>
                {feeTotal && (
                  <ListItem>
                    Total course fees: <b>£{feeTotal?.toLocaleString()}</b>
                  </ListItem>
                )}
                {offer?.programme?.scholarships?.length > 0 && (
                  <ListItem>
                    Scholarships:{' '}
                    <b>{offer.programme.scholarships.map(s => formatApplyScholarship(s, fee)).join(' & ')}</b>
                  </ListItem>
                )}
              </UnorderedList>
              <Link onClick={toggleDetails} display="block" mt={2}>
                Hide details
              </Link>
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
};
